import { useEffect, useState } from "react";
import { Link, Navigate, Route, Routes } from "react-router-dom";
import { api } from "./api";
import { AuthProvider, RequireAuth, useAuth } from "./auth";
import LoginPage from "./pages/LoginPage";
import MultiagentListPage from "./pages/MultiagentListPage";
import MultiagentRunPage from "./pages/MultiagentRunPage";
import MultiagentUploadPage from "./pages/MultiagentUploadPage";
import LvListPage from "./pages/LvListPage";
import LvPage from "./pages/LvPage";

function HeaderBar({ lvEnabled }: { lvEnabled: boolean }) {
  const { state, logout } = useAuth();
  const username =
    state.status === "authenticated" ? state.username : null;
  return (
    <header
      className="border-b flex items-center gap-6 px-5 py-3"
      style={{
        background: "var(--bd-surface)",
        borderColor: "var(--bd-line)",
      }}
    >
      <Link to="/" className="flex items-center gap-2.5 no-underline">
        <span
          className="flex items-center justify-center text-white font-bold"
          style={{
            width: 24,
            height: 24,
            borderRadius: 5,
            background: "var(--bd-ember)",
            fontSize: 13,
            boxShadow:
              "0 1px 0 rgba(255,255,255,0.4) inset, 0 4px 8px color-mix(in oklab, var(--bd-ember) 35%, transparent)",
          }}
        >
          b
        </span>
        <span
          className="font-semibold"
          style={{ fontSize: 14, color: "var(--bd-text)", letterSpacing: -0.2 }}
        >
          bidi · takeoff
        </span>
        <span
          className="ml-1"
          style={{
            fontSize: 10,
            color: "var(--bd-text-3)",
            letterSpacing: 0.4,
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          Atelier
        </span>
      </Link>
      <nav className="flex items-center gap-1 text-sm">
        {[
          { to: "/", label: "Projekte" },
          ...(lvEnabled ? [{ to: "/lv", label: "LV" }] : []),
        ].map((l) => (
          <Link
            key={l.to}
            to={l.to}
            className="px-2.5 py-1 rounded no-underline"
            style={{ color: "var(--bd-text-2)", fontSize: 13 }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--bd-surface2)";
              e.currentTarget.style.color = "var(--bd-text)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = "var(--bd-text-2)";
            }}
          >
            {l.label}
          </Link>
        ))}
      </nav>
      <div className="ml-auto flex items-center gap-3">
        {username && (
          <>
            <span style={{ fontSize: 11, color: "var(--bd-text-3)" }}>
              {username}
            </span>
            <button
              onClick={() => {
                logout().then(() => {
                  // Hard-Reload, damit alle in-flight-States verworfen werden.
                  window.location.href = "/login";
                });
              }}
              className="px-2 py-1 rounded"
              style={{
                fontSize: 11,
                color: "var(--bd-text-2)",
                background: "var(--bd-surface2)",
                border: "1px solid var(--bd-line)",
              }}
              title="Abmelden"
            >
              Abmelden
            </button>
          </>
        )}
      </div>
    </header>
  );
}

function ProtectedShell({ lvEnabled }: { lvEnabled: boolean }) {
  return (
    <RequireAuth>
      <div className="min-h-full flex flex-col">
        <HeaderBar lvEnabled={lvEnabled} />
        <main className="flex-1" style={{ background: "var(--bd-bg)" }}>
          <Routes>
            <Route path="/" element={<MultiagentListPage />} />
            <Route path="/upload" element={<MultiagentUploadPage />} />
            <Route path="/multiagent" element={<Navigate to="/" replace />} />
            <Route
              path="/multiagent/upload"
              element={<Navigate to="/upload" replace />}
            />
            <Route path="/multiagent/:runId" element={<MultiagentRunPage />} />
            {lvEnabled && <Route path="/lv" element={<LvListPage />} />}
            {lvEnabled && <Route path="/lv/:lvRunId" element={<LvPage />} />}
          </Routes>
        </main>
      </div>
    </RequireAuth>
  );
}

export default function App() {
  const [lvEnabled, setLvEnabled] = useState(false);
  useEffect(() => {
    api
      .featureFlags()
      .then((flags) => setLvEnabled(flags.lv))
      .catch(() => setLvEnabled(false));
  }, []);

  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<ProtectedShell lvEnabled={lvEnabled} />} />
      </Routes>
    </AuthProvider>
  );
}
